import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { messageLisible } from '@/shared/api/erreurs';
import {
  useCoupons,
  useCreerCoupon,
  useDesactiverCoupon,
  type CouponDto,
} from '@/shared/api/hooksAdmin';
import { ACTES } from '@/shared/audit/actesSensibles';
import { DialogueActeSensible } from '@/shared/audit/DialogueActeSensible';
import { date, entier } from '@/shared/lib/utils';
import { Bouton } from '@/shared/ui/Bouton';
import { Aide, Etiquette, ErreurChamp, Saisie } from '@/shared/ui/Champ';
import {
  ContenuDialogue,
  CorpsDialogue,
  DescriptionDialogue,
  Dialogue,
  EnTeteDialogue,
  PiedDialogue,
  TitreDialogue,
} from '@/shared/ui/Dialogue';
import { Carte, EnTeteSection, Pastille, Table, type ColonneTable } from '@/shared/ui/Table';

/**
 * Coupons de remise.
 *
 * L'admin PILOTE et AFFICHE ; la mecanique de remise est celle de Stripe (§4). Les regles
 * de validite — code deja pris, quota epuise, date depassee — sont verifiees par l'API,
 * pas ici : ce formulaire se contente d'empecher une saisie manifestement vide, et relaie
 * le refus du serveur tel quel plutot que d'inventer un diagnostic.
 */
export default function CouponsPage() {
  const { data, isPending, isError, error, refetch } = useCoupons();
  const [creation, setCreation] = useState(false);
  const [aDesactiver, setADesactiver] = useState<CouponDto | null>(null);

  const desactiver = useDesactiverCoupon();

  const colonnes: readonly ColonneTable<CouponDto>[] = [
    {
      cle: 'code',
      entete: 'Code',
      rendu: (ligne) => <code className="font-medium text-texte">{ligne.code}</code>,
    },
    {
      cle: 'remise',
      entete: 'Remise',
      rendu: (ligne) =>
        ligne.type === 'Pourcentage' ? `${ligne.valeur} %` : `${ligne.valeur} €`,
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (ligne) => (
        <Pastille ton={ligne.statut === 'Actif' ? 'succes' : 'neutre'}>{ligne.statut}</Pastille>
      ),
    },
    {
      cle: 'usage',
      entete: 'Usage',
      numerique: true,
      rendu: (ligne) => (
        <span className="text-texte-doux">
          {entier(ligne.usageCourant)}
          {ligne.usageMax ? ` / ${entier(ligne.usageMax)}` : ''}
        </span>
      ),
    },
    {
      cle: 'dureeMois',
      entete: 'Duree',
      rendu: (ligne) => (
        <span className="text-texte-doux">
          {ligne.dureeMois ? `${entier(ligne.dureeMois)} mois` : 'Illimitee'}
        </span>
      ),
    },
    {
      cle: 'dateMax',
      entete: 'Valable jusqu’au',
      secondaire: true,
      rendu: (ligne) => <span className="text-texte-doux">{date(ligne.dateMax)}</span>,
    },
    {
      cle: 'actions',
      entete: '',
      rendu: (ligne) => (
        <div className="flex justify-end">
          {ligne.statut === 'Actif' ? (
            <Bouton variante="discret" taille="sm" onClick={() => setADesactiver(ligne)}>
              <Trash2 />
              Desactiver
            </Bouton>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <EnTeteSection
        titre="Coupons"
        description="Codes de remise sur l’abonnement SaaS."
        actions={
          <Bouton onClick={() => setCreation(true)}>
            <Plus />
            Nouveau coupon
          </Bouton>
        }
      />

      <Table
        colonnes={colonnes}
        lignes={data}
        cleLigne={(ligne) => ligne.couponId ?? ''}
        chargement={isPending}
        erreur={isError ? error : undefined}
        onReessayer={() => void refetch()}
        messageVide="Aucun coupon."
      />

      <Carte className="p-3">
        <p className="text-texte-doux">
          La remise elle-meme est appliquee par Stripe. Astrolabe declare le coupon et suit
          son usage ; il ne calcule aucun montant.
        </p>
      </Carte>

      {creation ? <DialogueCreation onFermer={() => setCreation(false)} /> : null}

      {aDesactiver ? (
        <DialogueActeSensible
          acte={ACTES.couponSupprime}
          cible={aDesactiver.code ?? ''}
          enCours={desactiver.isPending}
          onFermer={() => setADesactiver(null)}
          onConfirmer={() =>
            desactiver.mutate(aDesactiver.couponId as string, {
              onSuccess: () => {
                toast.success('Coupon desactive');
                setADesactiver(null);
              },
              onError: (erreur) =>
                toast.error('Desactivation impossible', { description: messageLisible(erreur) }),
            })
          }
        />
      ) : null}
    </div>
  );
}

function DialogueCreation({ onFermer }: { onFermer: () => void }) {
  const [code, setCode] = useState('');
  const [pourcentage, setPourcentage] = useState(true);
  const [valeur, setValeur] = useState('10');
  const [usageMax, setUsageMax] = useState('');
  const [dateMax, setDateMax] = useState('');

  /*
   * -1 = ILLIMITE, et c'est ecrit sous le champ.
   *
   * Un champ numerique n'a pas de case « pour toujours », et le laisser vide se
   * confond avec « pas encore rempli ». La convention est traduite cote API : le
   * domaine, lui, ne connait qu'une duree presente ou absente.
   */
  const [dureeMois, setDureeMois] = useState('-1');
  const [erreur, setErreur] = useState<string | null>(null);

  const creer = useCreerCoupon();

  const valeurNumerique = Number(valeur.replace(',', '.'));
  const valide = code.trim().length >= 3 && Number.isFinite(valeurNumerique) && valeurNumerique > 0;

  return (
    <Dialogue open onOpenChange={(ouvert) => !ouvert && onFermer()}>
      <ContenuDialogue>
        <EnTeteDialogue>
          <TitreDialogue>Nouveau coupon</TitreDialogue>
          <DescriptionDialogue>
            Le code est saisi par le client au moment de souscrire.
          </DescriptionDialogue>
        </EnTeteDialogue>

        <CorpsDialogue className="space-y-3">
          <div className="space-y-1.5">
            <Etiquette htmlFor="coupon-code">Code</Etiquette>
            <Saisie
              id="coupon-code"
              value={code}
              onChange={(evenement) => setCode(evenement.target.value.toUpperCase())}
              placeholder="BIENVENUE2026"
              autoComplete="off"
              autoFocus
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1 space-y-1.5">
              <Etiquette htmlFor="coupon-valeur">Remise</Etiquette>
              <Saisie
                id="coupon-valeur"
                type="number"
                min="0.01"
                step="0.01"
                value={valeur}
                onChange={(evenement) => setValeur(evenement.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Etiquette htmlFor="coupon-type">Type</Etiquette>
              <div className="flex gap-1" id="coupon-type">
                <Bouton
                  type="button"
                  variante={pourcentage ? 'secondaire' : 'discret'}
                  aria-pressed={pourcentage}
                  onClick={() => setPourcentage(true)}
                >
                  %
                </Bouton>
                <Bouton
                  type="button"
                  variante={!pourcentage ? 'secondaire' : 'discret'}
                  aria-pressed={!pourcentage}
                  onClick={() => setPourcentage(false)}
                >
                  €
                </Bouton>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex-1 space-y-1.5">
              <Etiquette htmlFor="coupon-usage">Utilisations maximum</Etiquette>
              <Saisie
                id="coupon-usage"
                type="number"
                min="1"
                value={usageMax}
                onChange={(evenement) => setUsageMax(evenement.target.value)}
                placeholder="illimite"
              />
            </div>

            <div className="flex-1 space-y-1.5">
              <Etiquette htmlFor="coupon-date">Valable jusqu’au</Etiquette>
              <Saisie
                id="coupon-date"
                type="date"
                value={dateMax}
                onChange={(evenement) => setDateMax(evenement.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Etiquette htmlFor="coupon-duree">Duree de la remise (mois)</Etiquette>
            <Saisie
              id="coupon-duree"
              type="number"
              min="-1"
              value={dureeMois}
              onChange={(evenement) => setDureeMois(evenement.target.value)}
            />
            <Aide>
              Nombre de prelevements sur lesquels la remise s’applique. <strong>-1 = illimite</strong>.
              A ne pas confondre avec « valable jusqu’au », qui borne la date de saisie du code.
            </Aide>
          </div>

          <ErreurChamp>{erreur}</ErreurChamp>
          <Aide>Les regles de validite sont verifiees par l’API.</Aide>
        </CorpsDialogue>

        <PiedDialogue>
          <Bouton variante="secondaire" onClick={onFermer}>
            Annuler
          </Bouton>

          <Bouton
            disabled={!valide || creer.isPending}
            onClick={() => {
              setErreur(null);

              creer.mutate(
                {
                  code: code.trim(),
                  type: pourcentage ? 'Pourcentage' : 'Montant',
                  valeur: valeurNumerique,
                  dateMax: dateMax ? new Date(dateMax).toISOString() : null,
                  usageMax: usageMax ? Number(usageMax) : null,
                  dureeMois: dureeMois ? Number(dureeMois) : -1,
                },
                {
                  onSuccess: () => {
                    toast.success('Coupon cree');
                    onFermer();
                  },
                  onError: (cause) => setErreur(messageLisible(cause)),
                },
              );
            }}
          >
            Creer le coupon
          </Bouton>
        </PiedDialogue>
      </ContenuDialogue>
    </Dialogue>
  );
}
